import { campaignSelectionKey } from '@/api/campaignBudgetUpdate';
import { coerceDisplayString, mergeUnifiedKeywords, type KeywordEntityRow } from '@/api/unifiedKeywords';
import { normalizeSpCampaign, type SpCampaignNormalized } from '@/types/spCampaigns';
import type { Portfolio } from '@/types/portfolios';
import type { UnifiedAdGroupRow } from '@/utils/unifiedAdGroups';
import {
  PORTFOLIO_NONE,
  type AdGroupNode,
  type CampaignNode,
  type EntityGraph,
  type EntityGraphIndices,
  type KeywordPin,
  type PortfolioNode,
} from './types';

export function campaignPortfolioId(campaign: SpCampaignNormalized): string | null {
  const raw = campaign.raw;
  const v = raw.portfolioId ?? raw.portfolio_id;
  if (v == null || v === '') return null;
  const text = String(v).trim();
  return text || null;
}

export function portfolioIdForCampaign(campaign: CampaignNode): string {
  return campaign.portfolioId ?? PORTFOLIO_NONE;
}

export function campaignKeyForAdGroup(adProduct: string, campaignId: string): string {
  const product = adProduct === 'SB' || adProduct === 'SD' ? adProduct : 'SP';
  return `${product}-${campaignId}`;
}

function syntheticNoPortfolio(): Portfolio {
  return {
    portfolioId: PORTFOLIO_NONE,
    name: 'No portfolio',
    state: '—',
  };
}

function keywordPinsFromRow(row: ReturnType<typeof mergeUnifiedKeywords>[number]): KeywordPin[] {
  return row.sources
    .map((source) => {
      const keywordId = coerceDisplayString(source.keyword_id);
      const campaignId = coerceDisplayString(source.campaign_id);
      const adGroupId = coerceDisplayString(source.ad_group_id);
      if (!keywordId) return null;
      return {
        keywordId,
        adProduct: source.adProduct,
        campaignId,
        adGroupId,
      };
    })
    .filter((pin): pin is KeywordPin => pin != null);
}

export function buildEntityGraph(input: {
  portfolios: Portfolio[];
  campaigns: SpCampaignNormalized[];
  adGroups: UnifiedAdGroupRow[];
  spKeywords: KeywordEntityRow[];
  sbKeywords: KeywordEntityRow[];
}): EntityGraph {
  const campaignNodes: CampaignNode[] = input.campaigns.map((raw) => ({
    campaignKey: campaignSelectionKey(raw),
    campaignId: raw.campaignId,
    adProduct: raw.adProduct,
    name: raw.name,
    state: raw.state,
    portfolioId: campaignPortfolioId(raw),
    raw,
  }));

  const hasNoPortfolioCampaign = campaignNodes.some((c) => c.portfolioId == null);
  const portfolioNodes: PortfolioNode[] = input.portfolios.map((p) => ({
    portfolioId: p.portfolioId,
    name: p.name,
    state: p.state,
    raw: p,
  }));
  if (hasNoPortfolioCampaign) {
    portfolioNodes.push({
      portfolioId: PORTFOLIO_NONE,
      name: 'No portfolio',
      state: '—',
      raw: syntheticNoPortfolio(),
    });
  }

  const adGroupNodes: AdGroupNode[] = input.adGroups.map((ag) => ({
    adGroupId: ag.adGroupId,
    campaignId: ag.campaignId,
    adProduct: ag.adProduct,
    name: ag.name,
    state: ag.state,
    servingStatus: ag.servingStatus,
    raw: ag.raw,
  }));

  const keywords = mergeUnifiedKeywords(input.spKeywords, input.sbKeywords);

  const campaignsByKey = new Map<string, CampaignNode>();
  const campaignsByPortfolioId = new Map<string, CampaignNode[]>();
  const portfolioIdByCampaignKey = new Map<string, string>();

  for (const campaign of campaignNodes) {
    campaignsByKey.set(campaign.campaignKey, campaign);
    const pid = portfolioIdForCampaign(campaign);
    portfolioIdByCampaignKey.set(campaign.campaignKey, pid);
    const list = campaignsByPortfolioId.get(pid) ?? [];
    list.push(campaign);
    campaignsByPortfolioId.set(pid, list);
  }

  const adGroupsById = new Map<string, AdGroupNode>();
  const adGroupsByCampaignKey = new Map<string, AdGroupNode[]>();
  for (const adGroup of adGroupNodes) {
    adGroupsById.set(adGroup.adGroupId, adGroup);
    const ck = campaignKeyForAdGroup(adGroup.adProduct, adGroup.campaignId);
    const list = adGroupsByCampaignKey.get(ck) ?? [];
    list.push(adGroup);
    adGroupsByCampaignKey.set(ck, list);
  }

  const keywordPinsByGroupKey = new Map<string, KeywordPin[]>();
  const keywordIdsByGroupKey = new Map<string, string[]>();
  const allKeywordIds = new Set<string>();

  for (const row of keywords) {
    const pins = keywordPinsFromRow(row);
    keywordPinsByGroupKey.set(row.groupKey, pins);
    const ids = pins.map((p) => p.keywordId);
    keywordIdsByGroupKey.set(row.groupKey, ids);
    for (const id of ids) allKeywordIds.add(id);
  }

  const indices: EntityGraphIndices = {
    campaignsByKey,
    campaignsByPortfolioId,
    adGroupsById,
    adGroupsByCampaignKey,
    keywordPinsByGroupKey,
    keywordIdsByGroupKey,
    portfolioIdByCampaignKey,
    allKeywordIds,
  };

  return {
    portfolios: portfolioNodes,
    campaigns: campaignNodes,
    adGroups: adGroupNodes,
    keywords,
    indices,
  };
}

export function parseCampaignsPayload(data: unknown): SpCampaignNormalized[] {
  if (Array.isArray(data)) {
    return data
      .filter((x): x is Record<string, unknown> => x !== null && typeof x === 'object')
      .map((x) => normalizeSpCampaign(x));
  }
  if (!data || typeof data !== 'object') return [];
  const d = data as Record<string, unknown>;
  const rawList = (d.campaigns as unknown[]) || (d.items as unknown[]) || [];
  if (!Array.isArray(rawList)) return [];
  return rawList
    .filter((x): x is Record<string, unknown> => x !== null && typeof x === 'object')
    .map((x) => normalizeSpCampaign(x));
}
